import { useCallback, useContext, useMemo, useState } from 'react';
import clsx from 'clsx';
import { isEmpty, keyBy, pickBy, sortBy } from 'lodash-es';
import { Box } from '@alf/uikit';
import { AllTypesBonusesList } from '@namespace/bonuses';
import { useConfig, useGeneralRoutes, useOtherSettings } from '@namespace/cms';
import { useI18n, useLocation } from '@namespace/i18n';
import { useRouteParams } from '@namespace/router';
import { useUserSegmentsIncludes } from '@namespace/segments';
import { useThemeComponents } from '@namespace/themes';
import { USER_ROLES, UserContext } from '@namespace/user';
import DefaultBonusHunterPromotions from '../../common/BonusHunterPromotions';
import { PromotionsBonusItemWrapper } from '../../common/PromotionsBonusItemWrapper';
import { PromotionsItemWrapper } from '../../common/PromotionsItemWrapper';
import { PromotionsTournamentItem } from '../../common/PromotionsTournamentItem';
import { PromotionsWidget } from '../../common/PromotionsWidget';
import {
  BONUSES_SECTION_ACTIVE,
  BONUSES_SECTION_AVAILABLE,
  DEFAULT_ACTIVE_TAG,
  DEFAULT_PROMO_TYPE,
  PROMO_TYPES
} from '../../constants';
import { usePromotionsRoutes } from '../../hooks';
import { usePromotionsStore } from '../../store';
import {
  normalizePromoTabs,
  parsePromoBonusesApi,
  parsePromotionsApi
} from '../../store/normalize';
import getValidTags from '../../utils/getValidTags';
import {
  BonusesSectionToggle,
  BonusesSkelet,
  Categories,
  PromotionsEmptyState as DefaultPromotionsEmptyState,
  PromotionsSkelet,
  PromotionsSplitSkelet,
  Tags,
  TournamentsSkelet
} from './components';

import styles from './index.module.css';

const promoTypeToItemComponent = {
  [PROMO_TYPES.promotion]: PromotionsItemWrapper,
  [PROMO_TYPES.tournament]: PromotionsTournamentItem,
  [PROMO_TYPES.bonus]: PromotionsBonusItemWrapper,
  [PROMO_TYPES.widget]: PromotionsWidget
};

const promoTypeToSkeletonComponent = {
  [PROMO_TYPES.promotion]: PromotionsSkelet,
  [PROMO_TYPES.tournament]: TournamentsSkelet,
  [PROMO_TYPES.bonus]: BonusesSkelet,
  [PROMO_TYPES.widget]: PromotionsSkelet
};

const promoTypeToContainerClass = {
  [PROMO_TYPES.promotion]: styles.promotionsContainer,
  [PROMO_TYPES.tournament]: styles.tournamentsContainer,
  [PROMO_TYPES.bonus]: styles.bonusesContainer
};

const Promotions = ({
  id,
  isNeedFullCoverEmpty = false,
  isNeedSplittedPromosSkeleton = false,
  isNeedToShowTags = true
}) => {
  const [promoItemActiveId, setPromoItemActiveId] = useState(null);
  const { splitCategories = [] } = useOtherSettings();
  const [user] = useContext(UserContext);
  const { accountStatuses = {}, role } = user;
  const { isBonusHunter, isAddictedUser } = accountStatuses;
  const {
    PromotionsEmptyState = DefaultPromotionsEmptyState,
    BonusHunterPromotions = DefaultBonusHunterPromotions,
    PromotionsTournamentsLobby: PartnerPromotionsTournamentsLobby,
    PromotionsLobby: PartnerPromotionsLobby = null
  } = useThemeComponents();
  const { t } = useI18n();
  const { promotions, isPromosLoaded, bonuses, isPromoBonusesLoaded } =
    usePromotionsStore([
      'promotions',
      'isPromosLoaded',
      'bonuses',
      'isPromoBonusesLoaded'
    ]);
  const { promotions: promotionsPath } = useGeneralRoutes();
  const { tag: tagPath } = useRouteParams(`${promotionsPath}/:category/:tag`);
  const { pathname } = useLocation();
  const { config = {} } = useConfig(id);
  const filteredSegments = useUserSegmentsIncludes();
  const normalizedPromoTabs = useMemo(
    () => normalizePromoTabs(config),
    [config]
  );
  const isLoggedInUser = useMemo(() => role === USER_ROLES.USER, [role]);

  const handleTogglePromoItem = useCallback(
    (promoId) => {
      const actualPromoId = promoId !== promoItemActiveId ? promoId : null;

      setPromoItemActiveId(actualPromoId);
    },
    [promoItemActiveId]
  );

  const enabledPromoTabs = useMemo(
    () => Object.keys(pickBy(normalizedPromoTabs, ({ enabled }) => enabled)),
    [normalizedPromoTabs]
  );

  // TODO: can use useGetActivePromoType ?
  // TODO: purpose of default type. can just redirect in usePromotionsRoutes in case of wrong type ?
  const activePromoType = useMemo(
    () =>
      enabledPromoTabs.find((item) => pathname.includes(item) && item) || [
        DEFAULT_PROMO_TYPE
      ],
    [enabledPromoTabs, pathname]
  );

  const isPromotionsPage = activePromoType === PROMO_TYPES.promotion;
  const isTournamentPage = activePromoType === PROMO_TYPES.tournament;
  const isBonusPage = activePromoType === PROMO_TYPES.bonus;
  const enabledTags = normalizedPromoTabs[activePromoType]?.categories;
  const isWidget = activePromoType === PROMO_TYPES.widget;
  const activeTag = enabledTags
    ? enabledTags[tagPath]?.categoryName
    : DEFAULT_ACTIVE_TAG;

  const isDisplayBonusesSectionToggle = useMemo(
    () => normalizedPromoTabs[activePromoType]?.showAvailableActiveCategories,
    [activePromoType, normalizedPromoTabs]
  );

  const listOfSelectedPromo =
    normalizedPromoTabs[activePromoType]?.categories[activeTag]?.list || [];

  const parsedPromos = useMemo(
    () =>
      parsePromotionsApi(
        promotions,
        activePromoType,
        filteredSegments,
        listOfSelectedPromo
      ),
    [activePromoType, filteredSegments, listOfSelectedPromo, promotions]
  );

  const parsedBonuses = useMemo(
    () => parsePromoBonusesApi(bonuses, filteredSegments, listOfSelectedPromo),
    [bonuses, filteredSegments, listOfSelectedPromo]
  );

  const activeItems = isBonusPage ? parsedBonuses : parsedPromos;
  const activePromotions = Object.keys(listOfSelectedPromo)
    .map((promoId) => keyBy(activeItems, 'promoId')[promoId] || false)
    .filter(Boolean);

  const sortedPromotions = isBonusPage
    ? sortBy(activePromotions, [
        (bonus) => (bonus.isBonusFinished ? 1 : 0),
        (bonus) => (bonus.isBonusActive ? 1 : 0)
      ])
    : activePromotions;

  const validTags = enabledTags
    ? getValidTags(
        enabledTags,
        normalizedPromoTabs,
        promotions,
        activePromoType,
        filteredSegments
      )
    : [];
  const isShowTags = Object.keys(validTags).length > 1 && isNeedToShowTags;

  const PromotionItemComponent = promoTypeToItemComponent[activePromoType];
  const PromotionSkeletonComponent =
    promoTypeToSkeletonComponent[activePromoType];

  usePromotionsRoutes({
    enabledPromoTabs,
    enabledTags,
    validTags,
    isDisplayBonusesSectionToggle
  });

  if (isBonusHunter || isAddictedUser) {
    return (
      <BonusHunterPromotions
        activePromoType={activePromoType}
        enabledTypes={enabledPromoTabs}
      />
    );
  }

  if (!isPromosLoaded || (!isPromoBonusesLoaded && isBonusPage)) {
    return isNeedSplittedPromosSkeleton &&
      activePromoType === PROMO_TYPES.promotion &&
      splitCategories.length > 0 ? (
      <PromotionsSplitSkelet />
    ) : (
      <PromotionSkeletonComponent />
    );
  }

  if (
    enabledPromoTabs.length === 0 ||
    !pathname.includes(promotionsPath) ||
    !PromotionItemComponent
  ) {
    return null;
  }

  if (
    !activePromoType ||
    (isNeedFullCoverEmpty && (isEmpty(activePromotions) || isEmpty(validTags)))
  ) {
    return (
      <PromotionsEmptyState
        activePromoType={activePromoType}
        promotionTabs={normalizedPromoTabs}
        withPlayButton={isTournamentPage}
        enabledTypes={enabledPromoTabs}
      />
    );
  }

  if (isTournamentPage && PartnerPromotionsTournamentsLobby) {
    return (
      <PartnerPromotionsTournamentsLobby
        activePromotions={activePromotions}
        enabledTypes={enabledPromoTabs}
      />
    );
  }

  if (isPromotionsPage && PartnerPromotionsLobby) {
    return (
      <PartnerPromotionsLobby
        activePromotions={activePromotions}
        enabledTypes={enabledPromoTabs}
        splitCategories={splitCategories}
      />
    );
  }

  return (
    <Box
      direction="column"
      className={styles.wrapper}
      data-role="promotions-wrapper"
    >
      <Box className={styles.filtersContainer}>
        {enabledPromoTabs.length >= 2 && (
          <Categories
            enabledPromoTabs={enabledPromoTabs}
            normalizedPromoTabs={normalizedPromoTabs}
            activeCategory={activePromoType}
          />
        )}
        {isDisplayBonusesSectionToggle && isLoggedInUser ? (
          <div
            className={clsx([
              !(enabledPromoTabs.length >= 2) && styles.bonusSectionToggle
            ])}
          >
            <BonusesSectionToggle normalizedPromoTabs={normalizedPromoTabs} />
          </div>
        ) : (
          isShowTags && (
            <Tags
              validTags={validTags}
              activeTag={activeTag}
              activePromoType={activePromoType}
            />
          )
        )}
      </Box>
      {isDisplayBonusesSectionToggle &&
        tagPath === BONUSES_SECTION_ACTIVE &&
        !isWidget && (
          <AllTypesBonusesList
            CustomEmptyState={() => (
              <PromotionsEmptyState
                activePromoType={activePromoType}
                promotionTabs={normalizedPromoTabs}
                title={t('web.bonuses.active.placeholder')}
              />
            )}
            className={styles.bonusesListWrapper}
          />
        )}
      {!(
        isDisplayBonusesSectionToggle &&
        tagPath === BONUSES_SECTION_ACTIVE &&
        !isWidget
      ) && (
        <>
          {isEmpty(activePromotions) || isEmpty(validTags) ? (
            <PromotionsEmptyState
              activePromoType={activePromoType}
              promotionTabs={normalizedPromoTabs}
              withPlayButton={isTournamentPage}
              title={
                tagPath === BONUSES_SECTION_AVAILABLE &&
                activePromoType === PROMO_TYPES.bonus
                  ? t('web.bonuses.available.placeholder')
                  : ''
              }
            />
          ) : (
            <Box
              data-role="promotions-list-of-promos"
              align="center"
              className={promoTypeToContainerClass[activePromoType]}
            >
              {sortedPromotions.map((promo, index) => (
                <PromotionItemComponent
                  promo={promo}
                  key={promo.promoId}
                  tagPath={tagPath}
                  index={index + 1}
                  promoItemActiveId={promoItemActiveId}
                  togglePromoItem={handleTogglePromoItem}
                />
              ))}
            </Box>
          )}
        </>
      )}
      {isWidget ? <PromotionsWidget /> : null}
    </Box>
  );
};

export default Promotions;
